import { z } from 'zod';
import { calculerTotalCommande, totalDiverge, type PrixCommande } from './commande.ts';
import { analyserOrdonnance, type OrdonnanceCommande } from './ordonnance.ts';
import { normaliserDate, normaliserMontant } from './parsing.ts';

/**
 * Champs cibles de l'import Excel. Les cellules arrivent en **texte brut**,
 * exactement telles que lues dans la feuille : c'est sur ce texte que porte le
 * calcul de `import_hash` (règle 13), pour que la déduplication reste stable
 * même si l'analyse des prix ou des ordonnances évolue ensuite.
 */
export const CHAMPS_IMPORT = [
  'numero_bon',
  'date_commande',
  'client_nom',
  'client_tel',
  'od_raw',
  'og_raw',
  'addition',
  'type_verre',
  'prix_verre',
  'monture',
  'prix_monture',
  'lentilles',
  'prix_lentilles',
  'total',
  'avance',
  'reste',
  'societe',
] as const;
export type ChampImport = (typeof CHAMPS_IMPORT)[number];

export const CHAMP_IMPORT_LABELS: Record<ChampImport, string> = {
  numero_bon: 'N° de Bon',
  date_commande: 'Date',
  client_nom: 'Nom & Prénom',
  client_tel: 'N° Tel',
  od_raw: 'OD',
  og_raw: 'OG',
  addition: 'ADD',
  type_verre: 'Type Verre',
  prix_verre: 'Prix Verre',
  monture: 'Monture',
  prix_monture: 'Prix Monture',
  lentilles: 'Lentilles',
  prix_lentilles: 'Prix Lentilles',
  total: 'Total',
  avance: 'Avance',
  reste: 'Reste',
  societe: 'Société',
};

/** Champs sans lesquels une ligne n'a pas de sens. */
export const CHAMPS_IMPORT_REQUIS: readonly ChampImport[] = ['date_commande', 'client_nom'];

/**
 * En-têtes de sa feuille, pré-mappés par défaut. La clé est l'en-tête
 * normalisé (minuscules, sans accent, sans ponctuation, espaces réduits).
 */
export const MAPPING_PAR_DEFAUT: Record<string, ChampImport> = {
  'date': 'date_commande',
  'nom prenom': 'client_nom',
  'nom et prenom': 'client_nom',
  'nom': 'client_nom',
  'n tel': 'client_tel',
  'tel': 'client_tel',
  'telephone': 'client_tel',
  'n de bon': 'numero_bon',
  'n bon': 'numero_bon',
  'bon': 'numero_bon',
  'od': 'od_raw',
  'og': 'og_raw',
  'add': 'addition',
  'addition': 'addition',
  'type verre': 'type_verre',
  'type de verre': 'type_verre',
  'verre': 'type_verre',
  'prix verre': 'prix_verre',
  'monture': 'monture',
  'prix monture': 'prix_monture',
  'lentilles': 'lentilles',
  'lentille': 'lentilles',
  'prix lentilles': 'prix_lentilles',
  'prix lentille': 'prix_lentilles',
  'total': 'total',
  'avance': 'avance',
  'reste': 'reste',
  'societe': 'societe',
};

/** Normalise un en-tête de colonne pour la recherche dans MAPPING_PAR_DEFAUT. */
export function normaliserEntete(entete: string): string {
  return entete
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[°º]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Devine le champ cible d'un en-tête, ou null s'il est inconnu. */
export function deviner(entete: string): ChampImport | null {
  return MAPPING_PAR_DEFAUT[normaliserEntete(entete)] ?? null;
}

export const cellulesImportSchema = z.object(
  Object.fromEntries(CHAMPS_IMPORT.map((c) => [c, z.string().default('')])) as Record<
    ChampImport,
    z.ZodDefault<z.ZodString>
  >,
);
export type CellulesImport = z.infer<typeof cellulesImportSchema>;

/**
 * Forme canonique d'une ligne : clés triées, valeurs élaguées et en minuscules.
 *
 * Partagée volontairement : le serveur en tire `import_hash` (règle 13), le
 * client s'en sert pour numéroter les occurrences de lignes identiques. Deux
 * définitions divergentes casseraient l'idempotence, d'où une seule ici.
 */
export function canoniserCellules(cellules: CellulesImport): string {
  const champsTries = [...CHAMPS_IMPORT].sort();
  const objet: Record<string, string> = {};
  for (const champ of champsTries) {
    objet[champ] = (cellules[champ] ?? '').trim().toLowerCase();
  }
  return JSON.stringify(objet);
}

/**
 * Numérote chaque ligne selon son rang parmi les lignes strictement identiques
 * du fichier : 0 pour la première, 1 pour un doublon exact, etc.
 */
export function assignerOccurrences<T extends { cellules: CellulesImport }>(
  lignes: readonly T[],
): (T & { occurrence: number })[] {
  const vus = new Map<string, number>();
  return lignes.map((ligne) => {
    const cle = canoniserCellules(ligne.cellules);
    const occurrence = vus.get(cle) ?? 0;
    vus.set(cle, occurrence + 1);
    return { ...ligne, occurrence };
  });
}

/**
 * Plafond accepté par le serveur. Reste à 200 : le baisser ferait refuser d'un
 * coup les lots d'un navigateur encore servi par l'ancienne version en cache.
 */
export const TAILLE_LOT_IMPORT = 200;

/**
 * Ce que le navigateur envoie réellement.
 *
 * Moitié moins que le plafond, parce qu'un lot n'est pas une requête mais une
 * transaction : 200 lignes produisent jusqu'à 600 écritures qui doivent passer
 * ou échouer ensemble. Contre le fichier local — un simple fichier SQLite —
 * cela ne coûtait rien ; contre la vraie base, cette cadence a fini par se
 * faire refuser en 503 après une trentaine de lots. On envoie donc des
 * transactions plus courtes, et on souffle entre deux.
 */
export const TAILLE_LOT_ENVOI = 100;

/** Pause entre deux lots — laisse la base respirer sur un gros import. */
export const PAUSE_ENTRE_LOTS_MS = 150;

/**
 * Un lot refusé est réessayé : l'import est idempotent (règle 13), donc rejouer
 * un lot déjà passé n'insère rien. Sans reprise, un seul refus passager
 * abandonnait les quarante lots suivants.
 */
export const TENTATIVES_LOT = 4;
export const ATTENTE_REPRISE_MS = 600;

export const importRowSchema = z.object({
  /**
   * Rang de cette ligne parmi les lignes **strictement identiques** du fichier
   * (0 pour la première). C'est ce qui permet à une feuille contenant deux fois
   * la même ligne d'importer deux commandes, tout en gardant le réimport du
   * même fichier idempotent (règle 13).
   */
  occurrence: z.number().int().min(0).default(0),
  /** Numéro de ligne dans la feuille, pour les messages d'erreur. */
  ligne: z.number().int().min(1),
  cellules: cellulesImportSchema,
  /** La ligne d'origine complète — jamais perdue (`import_raw`). */
  import_raw: z.record(z.unknown()).default({}),
});
export type ImportRow = z.infer<typeof importRowSchema>;

export const importBatchSchema = z.object({
  rows: z
    .array(importRowSchema)
    .min(1, 'Aucune ligne à importer')
    .max(TAILLE_LOT_IMPORT, `Maximum ${TAILLE_LOT_IMPORT} lignes par lot`),
});
export type ImportBatch = z.infer<typeof importBatchSchema>;

export type ImportErreur = { ligne: number; message: string };

export type ImportResultat = {
  inserees: number;
  deja_presentes: number;
  a_verifier: number;
  clients_crees: number;
  erreurs: ImportErreur[];
};

/** Tolérance de comparaison des montants : un demi-centime. */
export const EPSILON_MONTANT = 0.005;

/**
 * Contrôle d'une ligne importée — l'unique référence, partagée par l'aperçu du
 * navigateur et par le service d'import.
 *
 * Aucune ligne n'est jamais rejetée : les anomalies deviennent des `motifs`, la
 * commande part avec `a_verifier = 1`, et la valeur d'origine est conservée.
 * Sur les montants, **la feuille fait foi** (règle 8) : un total qui ne colle
 * pas à la somme des postes est signalé, jamais recalculé.
 */
export function controlerLigneImport(cellules: CellulesImport): {
  motifs: string[];
  date: string | null;
  nom: string;
  tel: string | null;
  total: number;
  avance: number | null;
  prix: PrixCommande;
  ordonnance: OrdonnanceCommande;
} {
  const motifs: string[] = [];

  const date = normaliserDate(cellules.date_commande);
  if (!date) motifs.push(MOTIFS_VERIFICATION.DATE);

  const nom = cellules.client_nom.trim();
  if (nom === '') motifs.push(MOTIFS_VERIFICATION.CLIENT);

  const prix: PrixCommande = {
    prix_verre: normaliserMontant(cellules.prix_verre),
    prix_monture: normaliserMontant(cellules.prix_monture),
    prix_lentilles: normaliserMontant(cellules.prix_lentilles),
  };

  // Un prix écrit mais illisible mérite un contrôle ; une case vide, non.
  const prixIllisible = (
    [
      [cellules.prix_verre, prix.prix_verre],
      [cellules.prix_monture, prix.prix_monture],
      [cellules.prix_lentilles, prix.prix_lentilles],
    ] as const
  ).some(([brut, valeur]) => brut.trim() !== '' && valeur === null);
  if (prixIllisible) motifs.push(MOTIFS_VERIFICATION.PRIX);

  const totalFeuille = normaliserMontant(cellules.total);
  const total = totalFeuille ?? calculerTotalCommande(prix);
  if (totalFeuille !== null && totalDiverge(totalFeuille, prix)) {
    motifs.push(MOTIFS_VERIFICATION.TOTAL);
  }

  const avance = normaliserMontant(cellules.avance);
  const resteFeuille = normaliserMontant(cellules.reste);
  if (resteFeuille !== null && Math.abs(total - (avance ?? 0) - resteFeuille) > EPSILON_MONTANT) {
    motifs.push(MOTIFS_VERIFICATION.RESTE);
  }

  const ordonnance = analyserOrdonnance(
    cellules.od_raw || null,
    cellules.og_raw || null,
    cellules.addition || null,
  );
  if (ordonnance.aVerifier) {
    if (cellules.od_raw.trim() && !ordonnance.od_sph) motifs.push(MOTIFS_VERIFICATION.ORDONNANCE_OD);
    if (cellules.og_raw.trim() && !ordonnance.og_sph) motifs.push(MOTIFS_VERIFICATION.ORDONNANCE_OG);
    if (cellules.addition.trim() && !ordonnance.addition) {
      motifs.push(MOTIFS_VERIFICATION.ADDITION);
    }
  }

  return {
    motifs,
    date,
    nom: nom === '' ? 'Client sans nom' : nom,
    tel: cellules.client_tel.trim() || null,
    total,
    avance,
    prix,
    ordonnance,
  };
}

/** Motifs de contrôle affichés dans l'aperçu — la ligne est importée quand même. */
export const MOTIFS_VERIFICATION = {
  ORDONNANCE_OD: "L'ordonnance OD n'a pas pu être analysée",
  ORDONNANCE_OG: "L'ordonnance OG n'a pas pu être analysée",
  ADDITION: "L'addition n'a pas pu être analysée",
  PRIX: 'Un prix est illisible',
  DATE: 'Date absente ou illisible',
  TOTAL: 'Le total ne correspond pas à la somme des prix',
  RESTE: 'Le reste ne correspond pas à total moins avance',
  CLIENT: 'Nom du client absent',
} as const;
export type MotifVerification = (typeof MOTIFS_VERIFICATION)[keyof typeof MOTIFS_VERIFICATION];
